import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { Box, Divider, FormGroup, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ServiceHierarchyNode } from "../Domain/serviceNotificationHierarchy.types";
import {
  collectDescendantNodeIds,
  findHierarchyNode
} from "../functions/serviceNotificationHierarchy.helpers";
import { ApprovalLocationHierarchyRow } from "./ApprovalLocationHierarchyRow.partial";

export type ApprovalLocationHierarchyProps = {
  hierarchyRoot: ServiceHierarchyNode;
  enabled: Record<string, boolean>;
  setEnabled: Dispatch<SetStateAction<Record<string, boolean>>>;
};

export default function ApprovalLocationHierarchy({
  hierarchyRoot,
  enabled,
  setEnabled
}: Readonly<ApprovalLocationHierarchyProps>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpanded({});
  }, [hierarchyRoot]);

  const toggleExpanded = useCallback(
    (id: string) => {
      setExpanded(prev => {
        const isOpen = prev[id] === true;
        if (isOpen) {
          const node = findHierarchyNode(hierarchyRoot, id);
          const next = { ...prev, [id]: false };
          if (node) {
            for (const descId of collectDescendantNodeIds(node)) {
              delete next[descId];
            }
          }
          return next;
        }
        return { ...prev, [id]: true };
      });
    },
    [hierarchyRoot]
  );

  return (
    <Box sx={{ width: "100%", ml: 1 }} data-testid="approval-location-hierarchy">
      <Divider sx={{ my: 1, opacity: 0.6 }} />

      <Typography variant="subtitle2" sx={{ px: 2, pt: 1, pb: 0.5 }}>
        {t("_serviceNotificationHierarchy_sectionTitle")}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 2, pb: 1 }}
        data-testid="approval-location-hierarchy-section-hint"
      >
        {t("_serviceNotificationHierarchy_sectionHint")}
      </Typography>

      <FormGroup>
        <ApprovalLocationHierarchyRow
          node={hierarchyRoot}
          depth={0}
          enabled={enabled}
          setEnabled={setEnabled}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
        />
      </FormGroup>
    </Box>
  );
}
